import { SETTINGS_PERMISSIONS } from '@/constants/permissions';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import type { LabeledStat } from '@/components/common/StatCardGrid';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { getActiveStatCard, getInactiveStatCard } from '@/utils/activeStatCards';
import { feedbackTypesQueryOptions } from '../api/feedbackTypes.api';
import { useFeedbackTypeActionModals } from '../hooks/useFeedbackTypeActionModals';
import type { FeedbackType } from '../types/feedbackType.types';
import { getFeedbackTypeColumns } from './FeedbackTypeColumns';
import { FeedbackTypeDeleteConfirmation } from './FeedbackTypeDeleteConfirmation';
import { FeedbackTypeFormModal } from './FeedbackTypeFormModal';

const route = getRouteApi('/_auth/settings/feedback-types');

const toSearchableValues = (feedbackType: FeedbackType) => [
  feedbackType.name,
  feedbackType.code,
  feedbackType.description,
];

function getFeedbackTypeStatCards(feedbackTypes: FeedbackType[] | undefined): LabeledStat[] {
  const active = feedbackTypes?.filter((feedbackType) => feedbackType.isActive).length;
  const totals =
    feedbackTypes === undefined || active === undefined
      ? undefined
      : { active, inactive: feedbackTypes.length - active };
  return [
    { key: 'total', label: 'Feedback Types', value: feedbackTypes?.length },
    getActiveStatCard(totals),
    getInactiveStatCard(totals),
  ];
}

export function FeedbackTypeListTable() {
  const { q: appliedSearchText } = route.useSearch();
  const navigate = route.useNavigate();

  const {
    modalState,
    feedbackTypeToDelete,
    openCreateModal,
    openEditModal,
    closeModal,
    openDeleteModal,
    closeDeleteModal,
  } = useFeedbackTypeActionModals();

  const { data, isLoading, isRefetching, error, refetch } = useQuery(feedbackTypesQueryOptions());

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: data,
    appliedText: appliedSearchText,
    onApply: (text) => void navigate({ search: { q: text }, replace: true }),
    toSearchableValues,
  });

  const columns = useMemo(
    () =>
      getFeedbackTypeColumns(appliedSearchText, {
        onEdit: openEditModal,
        onDelete: openDeleteModal,
      }),
    [appliedSearchText, openEditModal, openDeleteModal],
  );

  return (
    <CatalogueListLayout
      title="Feedback Types"
      search={{
        value: searchText,
        onChange: setSearchText,
        placeholder: 'Search by code or name',
      }}
      stats={{
        cards: getFeedbackTypeStatCards(data),
        isLoading,
        isFetching: isRefetching,
        isError: error !== null,
      }}
      onAdd={openCreateModal}
      addLabel="Add Feedback Type"
      addRequires={SETTINGS_PERMISSIONS.FEEDBACK_TYPES_CREATE}
    >
      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load feedback types"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={visibleRows}
          columns={columns}
          isLoading={isLoading}
          stickyColumnId="name"
          className="lg:flex-1"
          emptyMessage="No feedback type matches"
        />
      )}

      <FeedbackTypeFormModal
        feedbackType={modalState?.feedbackType ?? null}
        open={modalState !== null}
        onOpenChange={(open) => !open && closeModal()}
      />

      <FeedbackTypeDeleteConfirmation
        feedbackType={feedbackTypeToDelete}
        open={feedbackTypeToDelete !== null}
        onOpenChange={(open) => !open && closeDeleteModal()}
      />
    </CatalogueListLayout>
  );
}
